import { Dispatch, SetStateAction, useEffect } from "react";
import { useQuery } from "react-query";

import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRowsProp,
} from "@mui/x-data-grid";

import { getLedgerFreezeRequest } from "../../../api/merchant";
import { OpRedirect } from "../../../assets/icons";
import { CustomPagination } from "../../../components/CustomPagination";
import LoadingDialog from "../../../components/LoadingDialog";
import { Box, Tooltip } from "../../../components/MuiGenerals";
import { NoRowsOverlay } from "../../../components/NoRowsOverlay";
import OpIconButton from "../../../components/OpIconButton";
import { useTranslation } from "../../../hooks";
import useColumnHide, {
  ColumnHiddenMessage,
  getColumnHideConfig,
} from "../../../hooks/useColumnHide";
import {
  dataGridDefaults,
  merchantOrderIdString,
} from "../../../utils/constant";
import { useGenGridCol } from "../../../utils/HelperComp";
import { useZusParams } from "./FreezeRequest";

const translatePrefix = "ledgerFreezeRequest";
export default function FreezeRequestList(props: {
  listMapping: (which: "key" | "name", array: any[], config?: any) => any[][];
  setCount: Dispatch<SetStateAction<number>>;
}) {
  const { t, te } = useTranslation(translatePrefix);
  const zusParams = useZusParams();
  const { listMapping, setCount } = props;
  const listRes = useQuery({
    queryKey: ["ledgerFreezeRequest", zusParams.body, zusParams.refetchCounter],
    queryFn: () => getLedgerFreezeRequest(zusParams.body),
  });
  const { rows = [], count = 0 } =
    (listRes.data as any as { rows: any[]; count: number }) || {};
  const content: GridRowsProp = listMapping("key", rows, {
    t,
    te,
  });

  useEffect(() => setCount(count), [listRes]);

  const RelatedRecords = (params: GridCellParams) => {
    const merchantOrderId = params?.row?.merchant_order_id;
    const toNewTab = () =>
      window.open(
        `/ledgerFrozenFund?${merchantOrderIdString}${merchantOrderId}`
      );
    if (!merchantOrderId) return <></>;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: ".5rem",
          width: "90%",
        }}
      >
        <Tooltip title={merchantOrderId}>
          <Box
            sx={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              maxWidth: "80%",
            }}
          >
            {merchantOrderId}
          </Box>
        </Tooltip>
        <OpIconButton
          title={""}
          url={OpRedirect}
          onClick={toNewTab}
          size="1rem"
        />
      </Box>
    );
  };

  const columns: GridColDef[] = [
    useGenGridCol("merchant_order_id", t("merchant_order_id"), {
      minWidth: 150,
      renderCell: RelatedRecords,
    }),
    useGenGridCol("approvals", t("approvals"), {
      renderCell: (params) => {
        const { approvals = 0, approvals_required = 0 } =
          params.row?.rowData || {};
        return (
          <>
            {approvals}/{approvals_required}
          </>
        );
      },
    }),
    useGenGridCol("client_id", t("client_id")),
    useGenGridCol("chain_name", t("chain_name")),
    useGenGridCol("currency", t("currency")),
    useGenGridCol("amount", t("amount")),
    useGenGridCol("seq", t("seq")),
    useGenGridCol("status", t("status"), { minWidth: 120 }),
    useGenGridCol("frozen_order_id", t("frozen_order_id"), {
      minWidth: 120,
    }),
    useGenGridCol("related_order_id", t("related_order_id"), { minWidth: 120 }),
    useGenGridCol("created_by", t("created_by")),
    useGenGridCol("creator_type", t("creator_type")),
    useGenGridCol("creation_time", t("creation_time")),
    useGenGridCol("last_modified_time", t("last_modified_time")),
    useGenGridCol("remarks", t("remarks")),
    useGenGridCol("error_reason", t("error_reason")),
  ];

  useEffect(() => () => zusParams.clear(), []);
  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    columns,
    getColumnHideConfig()
  );
  if (listRes.isLoading) return <LoadingDialog forceOpen={true} />;
  return (
    <>
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={content}
        rowCount={count}
        columns={columns}
        page={zusParams.body.page}
        onPageChange={zusParams.setPage}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords: count },
        }}
      />
    </>
  );
}
